import { PanelState, VisibilityLabels, VisibilityStates } from '@racemap/utilities/consts/events';
import { Col, Radio, Row, Tag } from 'antd';
import { useState } from 'react';
import { BasicPanel } from '../../BasicComponents/BasicPanel/BasicPanel';
import {
  IconVisibilityArchived,
  IconVisibilityDraft,
  IconVisibilityListed,
  IconVisibilityPublic,
  IconVisibilityUnlisted,
} from '../../BasicComponents/Icons';

interface Props {
  value: VisibilityStates;
  status: PanelState;
  onChange: (arg0: VisibilityStates) => void;
  authorized: boolean;
  possibleStates?: Array<VisibilityStates>;
  target?: string;
  disabled?: boolean;
}

export const VisibilityPanel = ({
  value,
  status,
  onChange,
  authorized,
  possibleStates = Object.values(VisibilityStates).filter((e) => e !== VisibilityStates.PUBLIC),
  target = 'event',
  disabled = false,
}: Props) => {
  const [hover, setHover] = useState<VisibilityStates | null | undefined>(null);
  const helpValue = hover != null ? hover : value;
  const IconVisibility = {
    DRAFT: <IconVisibilityDraft />,
    UNLISTED: <IconVisibilityUnlisted />,
    LISTED: <IconVisibilityListed />,
    ARCHIVED: <IconVisibilityArchived />,
    PUBLIC: <IconVisibilityPublic />,
  };
  const successState = status === PanelState.SUCCESS || status === PanelState.PRIMARY;

  const title = 'Visibility';
  const visibilityTag = (
    <Tag color="blue" icon={IconVisibility[value]}>
      {value.toLowerCase()}
    </Tag>
  );

  return (
    <div>
      <section>
        <Row>
          <Col span={24}>
            <BasicPanel
              forceOpen={!successState}
              defaultOpen={!successState}
              title={title}
              extra={visibilityTag}
            >
              <Radio.Group
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                optionType="button"
                block
              >
                {possibleStates.map((state) => (
                  <Radio.Button
                    key={state}
                    value={state}
                    disabled={disabled || state === VisibilityStates.DRAFT || !authorized}
                    onMouseEnter={() => setHover(state)}
                    onMouseLeave={() => setHover(null)}
                  >
                    {IconVisibility[state]} {VisibilityLabels[state]}
                  </Radio.Button>
                ))}
              </Radio.Group>
              <div style={{ padding: '8px' }}>{createHelpText(helpValue, authorized, target)}</div>
            </BasicPanel>
          </Col>
        </Row>
      </section>
    </div>
  );
};

const createHelpText = (visibilityState: VisibilityStates, authorized: boolean, target: string) => {
  switch (visibilityState) {
    case VisibilityStates.DRAFT:
      if (authorized && target !== 'stageGroup')
        return (
          <>
            Your Tracking Map is currently in a draft state, but already functioning. Make sure to
            set it to <strong>Unlisted, Listed or Open</strong> in order to activate your Tracking
            Map.
          </>
        );
      if (authorized && target === 'stageGroup')
        return (
          <>Set visibility status for your stage event, when Base Price of each stage is paid.</>
        );
      return (
        <>
          Test geolocation and live tracking in draft state of your event for free. You can use
          add-ons and adapt the settings to your use case.
        </>
      );
    case VisibilityStates.LISTED:
      if (target === 'contestGroup')
        return (
          <>
            We help you reach a large audience. Your Group/Stage Map is shown in our public event
            lists on the{' '}
            <a href="/events" target="_blank" rel="noopener noreferrer">
              Racemap website
            </a>{' '}
            and in the Racemap App. Additionally, you can share the link or embed the tracking map
            on other websites in order to expand your audience.
          </>
        );
      return (
        <>
          We help you reach a large audience. Your Tracking Map is shown in our public event lists
          on the{' '}
          <a href="/events" target="_blank" rel="noopener noreferrer">
            Racemap website
          </a>{' '}
          and in the Racemap App. Additionally, you can share the link or embed the tracking map on
          other websites in order to expand your audience.
        </>
      );
    case VisibilityStates.UNLISTED:
      if (target === 'contestGroup')
        return (
          <>
            Your Group/Stage Map is <strong>not</strong> listed in our public event lists on the{' '}
            <a href="/events" target="_blank" rel="noopener noreferrer">
              Racemap website
            </a>{' '}
            or in the Racemap App. Only people with access to the link will be able to view it. You
            can share the link and embed the Group/Stage Map on other websites in order to reach
            your target audience.
          </>
        );
      return (
        <>
          Your event is <strong>not</strong> listed in our public event lists on the{' '}
          <a href="/events" target="_blank" rel="noopener noreferrer">
            Racemap website
          </a>{' '}
          or in the Racemap App. Only people with access to the link will be able to view it. You
          can share the link and embed the tracking map on other websites in order to reach your
          target audience.
        </>
      );
    case VisibilityStates.ARCHIVED:
      if (target === 'contestGroup')
        return (
          <>
            There will no loads charged for an archived event. An archived event will not load the
            Group/Stage map, Leaderboard, Data API or display any other information. You can restore
            the event at any time.
          </>
        );
      return (
        <>
          There will no loads charged for an archived event. An archived event will not load the
          Tracking map, Leaderboard, Data API or display any other information. You can restore the
          event at any time.
        </>
      );
    case VisibilityStates.PUBLIC:
      return (
        <>
          We help you reach a large audience. Your Tracking Map is shown in our public event lists
          on the{' '}
          <a href="/events" target="_blank" rel="noopener noreferrer">
            Racemap website
          </a>{' '}
          and in the Racemap App. Additionally, you can share the link or embed the Tracking Map on
          another website in order to expand your audience. Everybody with the Racemap App will be
          able to join your Tracking Map. You may also add participants manually, with Keys or with
          the Passcode.
        </>
      );
    default:
      return <div />;
  }
};
